import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import type { LucideIcon } from 'lucide-react';
import {
  Bot,
  Database,
  FileText,
  FolderKanban,
  GitBranch,
  Layers3,
  Loader2,
  Plus,
  Search,
  Settings2,
  Sparkles,
  Trash2,
  Waypoints,
  Workflow,
  X,
  Zap,
} from 'lucide-react';
import {
  createWorkflowProject,
  deleteWorkflowProject,
  fetchWorkflowProjects,
  getWorkflowProject,
  updateWorkflowProject,
  workflowApiErrorMessage,
  type WorkflowProjectDto,
} from '../workflowbuilder/postWorkflowService';
import ConfirmDialog from '../Dialogs/ConfirmDialog';
import { useToast } from '../../../components/common/Toast';
import { apiService } from '../../../components/api/apiService';
import './WorkflowProjectsModal.css';

/** GET /api/auth/users */
type AuthUserDto = {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  tenantId?: string;
  roleID?: string;
  roleName?: string;
  isadmin?: boolean;
  isActive?: boolean;
};

function userDisplayName(u: AuthUserDto): string {
  const parts = [u.firstName?.trim(), u.lastName?.trim()].filter(Boolean);
  if (parts.length) return parts.join(' ');
  return u.email ?? '';
}

function formatUserOptionLabel(u: AuthUserDto): string {
  const name = userDisplayName(u);
  if (name && name !== u.email) return `${name} · ${u.email}`;
  return u.email;
}

/** Map API owner (id, email, or legacy label) to user id for form + PUT/POST. */
function resolveOwnerToUserId(raw: string | undefined, users: AuthUserDto[]): string {
  const t = (raw ?? '').trim();
  if (!t) return '';
  if (users.some((u) => u.id === t)) return t;
  const byEmail = users.find((u) => (u.email ?? '').toLowerCase() === t.toLowerCase());
  if (byEmail) return byEmail.id;
  const byName = users.find((u) => userDisplayName(u) === t);
  if (byName) return byName.id;
  return t;
}

function ownerDisplayLabel(raw: string | undefined, users: AuthUserDto[]): string {
  const t = (raw ?? '').trim();
  if (!t) return '';
  const byId = users.find((u) => u.id === t);
  if (byId) return formatUserOptionLabel(byId);
  const byEmail = users.find((u) => (u.email ?? '').toLowerCase() === t.toLowerCase());
  if (byEmail) return formatUserOptionLabel(byEmail);
  return t;
}

async function fetchAuthUsers(): Promise<AuthUserDto[]> {
  const response = await apiService.get('/api/auth/users', {}, 'backend');
  return Array.isArray(response) ? (response as AuthUserDto[]) : [];
}

const PROJECT_ICON_MAP: Record<string, LucideIcon> = {
  Workflow,
  GitBranch,
  Waypoints,
  Zap,
  Layers3,
  Sparkles,
  Bot,
  Settings2,
  Database,
  FileText,
  FolderKanban,
};

const PROJECT_ICON_OPTIONS: { value: string; label: string; Icon: LucideIcon }[] = [
  { value: 'Workflow', label: 'Workflow', Icon: Workflow },
  { value: 'GitBranch', label: 'Branch', Icon: GitBranch },
  { value: 'Waypoints', label: 'Waypoints', Icon: Waypoints },
  { value: 'Zap', label: 'Automation', Icon: Zap },
  { value: 'Layers3', label: 'Pipeline', Icon: Layers3 },
  { value: 'Sparkles', label: 'Smart', Icon: Sparkles },
  { value: 'Bot', label: 'Agent', Icon: Bot },
  { value: 'Settings2', label: 'Orchestrate', Icon: Settings2 },
  { value: 'Database', label: 'Data', Icon: Database },
  { value: 'FileText', label: 'Docs', Icon: FileText },
  { value: 'FolderKanban', label: 'Project', Icon: FolderKanban },
];

function ProjectRowIcon({ iconRef }: { iconRef: string | undefined }) {
  const name = (iconRef ?? 'FolderKanban').trim();
  const Icon = PROJECT_ICON_MAP[name] ?? FolderKanban;
  return <Icon className="w-[18px] h-[18px]" strokeWidth={1.5} aria-hidden />;
}

const STATUS_OPTIONS = ['Active', 'Archived', 'Draft'] as const;

type FormState = {
  name: string;
  description: string;
  owner: string;
  icon: string;
  status: string;
};

const emptyForm = (): FormState => ({
  name: '',
  description: '',
  owner: '',
  icon: 'FolderKanban',
  status: 'Active',
});

function projectToForm(p: WorkflowProjectDto, users: AuthUserDto[]): FormState {
  return {
    name: p.name ?? '',
    description: p.description ?? '',
    owner: resolveOwnerToUserId(p.owner, users),
    icon: PROJECT_ICON_MAP[p.icon ?? ''] ? (p.icon as string) : 'FolderKanban',
    status: p.status?.trim() || 'Active',
  };
}

export type WorkflowProjectsModalProps = {
  onClose: () => void;
};

const WorkflowProjectsModal: React.FC<WorkflowProjectsModalProps> = ({ onClose }) => {
  const { showToast } = useToast();
  const [projects, setProjects] = useState<WorkflowProjectDto[]>([]);
  const [loadingList, setLoadingList] = useState(true);
  const [search, setSearch] = useState('');
  const [form, setForm] = useState<FormState>(emptyForm);
  /** null = creating new; string = editing project uuid */
  const [editingUuid, setEditingUuid] = useState<string | null>(null);
  const [selectedUuid, setSelectedUuid] = useState<string | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [saving, setSaving] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState<WorkflowProjectDto | null>(null);
  const [users, setUsers] = useState<AuthUserDto[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(true);

  const usersSorted = useMemo(() => {
    return [...users].sort((a, b) =>
      formatUserOptionLabel(a).localeCompare(formatUserOptionLabel(b), undefined, { sensitivity: 'base' })
    );
  }, [users]);

  const loadProjects = useCallback(async () => {
    setLoadingList(true);
    try {
      const list = await fetchWorkflowProjects();
      setProjects(list);
    } catch (e) {
      showToast({ type: 'error', message: workflowApiErrorMessage(e) });
      setProjects([]);
    } finally {
      setLoadingList(false);
    }
  }, [showToast]);

  useEffect(() => {
    void loadProjects();
  }, [loadProjects]);

  useEffect(() => {
    let cancelled = false;
    setLoadingUsers(true);
    fetchAuthUsers()
      .then((list) => {
        if (!cancelled) setUsers(list);
      })
      .catch(() => {
        if (!cancelled) setUsers([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingUsers(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /** When users load after a project, normalize owner id/email for the form. */
  useEffect(() => {
    if (users.length === 0) return;
    setForm((f) => {
      const next = resolveOwnerToUserId(f.owner, users);
      return next === f.owner ? f : { ...f, owner: next };
    });
  }, [users]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return projects;
    return projects.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        (p.description ?? '').toLowerCase().includes(q) ||
        ownerDisplayLabel(p.owner, users).toLowerCase().includes(q)
    );
  }, [projects, search, users]);

  const resetToNew = () => {
    setEditingUuid(null);
    setSelectedUuid(null);
    setForm(emptyForm());
  };

  const selectProject = async (p: WorkflowProjectDto) => {
    setSelectedUuid(p.uuid);
    setEditingUuid(p.uuid);
    setLoadingDetail(true);
    try {
      const detail = await getWorkflowProject(p.uuid);
      if (detail) setForm(projectToForm(detail, users));
      else setForm(projectToForm(p, users));
    } catch (e) {
      showToast({ type: 'error', message: workflowApiErrorMessage(e) });
      setForm(projectToForm(p, users));
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const name = form.name.trim();
    if (!name) {
      showToast({ type: 'error', message: 'Project name is required.' });
      return;
    }

    setSaving(true);
    try {
      if (editingUuid) {
        await updateWorkflowProject(editingUuid, {
          name,
          description: form.description.trim(),
          owner: form.owner.trim(),
          icon: form.icon.trim() || 'FolderKanban',
          status: form.status.trim() || 'Active',
        });
        showToast({ type: 'success', message: 'Project updated.' });
      } else {
        const uuid =
          typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
            ? crypto.randomUUID()
            : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
        await createWorkflowProject({
          uuid,
          name,
          description: form.description.trim(),
          owner: form.owner.trim(),
          icon: form.icon.trim() || 'FolderKanban',
          status: form.status.trim() || 'Active',
        });
        showToast({ type: 'success', message: 'Project created.' });
        setEditingUuid(uuid);
        setSelectedUuid(uuid);
      }
      await loadProjects();
    } catch (err) {
      showToast({ type: 'error', message: workflowApiErrorMessage(err) });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!projectToDelete) return;
    try {
      await deleteWorkflowProject(projectToDelete.uuid);
      showToast({ type: 'success', message: 'Project deleted.' });
      setProjectToDelete(null);
      if (editingUuid === projectToDelete.uuid) resetToNew();
      await loadProjects();
    } catch (e) {
      showToast({ type: 'error', message: workflowApiErrorMessage(e) });
    }
  };

  const formTitle = editingUuid ? 'Edit project' : 'New project';

  return createPortal(
    <>
      <div className="wfp-backdrop" role="presentation" onMouseDown={onClose}>
        <div
          className="wfp-shell"
          role="dialog"
          aria-modal="true"
          aria-labelledby="wfp-dialog-title"
          onMouseDown={(ev) => ev.stopPropagation()}
        >
          <header className="wfp-head">
            <div>
              <p className="wfp-head__eyebrow">Post workflow</p>
              <h2 id="wfp-dialog-title" className="wfp-head__title">
                Manage projects
              </h2>
              <p className="wfp-head__sub">
                Organize orchestrations into projects. Create, update, or remove projects — changes apply immediately.
              </p>
            </div>
            <button type="button" className="wfp-close" onClick={onClose} aria-label="Close">
              <X className="w-5 h-5" strokeWidth={2} />
            </button>
          </header>

          <div className="wfp-body">
            <div className="wfp-list-pane">
              <div className="wfp-list-toolbar">
                <div className="wfp-search-wrap">
                  <Search className="wfp-search-icon" aria-hidden />
                  <input
                    type="search"
                    className="wfp-search"
                    placeholder="Search projects…"
                    value={search}
                    onChange={(ev) => setSearch(ev.target.value)}
                    aria-label="Search projects"
                  />
                </div>
                <button
                  type="button"
                  className="wfp-add-btn"
                  onClick={resetToNew}
                  disabled={loadingList}
                >
                  <Plus className="w-4 h-4" strokeWidth={2.5} aria-hidden />
                  New
                </button>
              </div>
              <div className="wfp-list-scroll">
                {loadingList ? (
                  <div className="wfp-list-empty">
                    <Loader2 className="wfp-spin inline w-6 h-6 opacity-70" aria-hidden />
                    <span className="block mt-2">Loading projects…</span>
                  </div>
                ) : filtered.length === 0 ? (
                  <div className="wfp-list-empty">
                    {projects.length === 0
                      ? 'No projects yet. Use New to create one.'
                      : 'No matches for your search.'}
                  </div>
                ) : (
                  filtered.map((p) => {
                    const active = (p.status ?? '').toLowerCase() === 'active';
                    const isSel = selectedUuid === p.uuid;
                    return (
                      <button
                        key={p.uuid}
                        type="button"
                        className={`wfp-row${isSel ? ' wfp-row--active' : ''}`}
                        onClick={() => void selectProject(p)}
                      >
                        <div className="wfp-row__icon" aria-hidden>
                          <ProjectRowIcon iconRef={p.icon} />
                        </div>
                        <div className="wfp-row__main">
                          <span className="wfp-row__name">{p.name}</span>
                          <span className="wfp-row__desc">
                            {(p.description ?? '').trim() || 'No description'}
                          </span>
                          <div className="wfp-row__meta">
                            <span className={`wfp-pill ${active ? 'wfp-pill--active' : 'wfp-pill--idle'}`}>
                              {p.status ?? '—'}
                            </span>
                            {(p.owner ?? '').trim() ? (
                              <span className="wfp-pill wfp-pill--idle" title={p.owner}>
                                {ownerDisplayLabel(p.owner, users)}
                              </span>
                            ) : null}
                          </div>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            </div>

            <div className="wfp-form-pane">
              <form id="wfp-project-form" className="wfp-form-inner" onSubmit={handleSubmit}>
                <p className="wfp-form-title">{formTitle}</p>

                {loadingDetail ? (
                  <div className="wfp-list-empty py-8">
                    <Loader2 className="wfp-spin inline w-6 h-6 opacity-70" aria-hidden />
                    <span className="block mt-2 text-sm">Loading project…</span>
                  </div>
                ) : (
                  <>
                    <div className="wfp-field">
                      <label className="wfp-label" htmlFor="wfp-name">
                        Name *
                      </label>
                      <input
                        id="wfp-name"
                        className="wfp-input"
                        value={form.name}
                        onChange={(ev) => setForm((f) => ({ ...f, name: ev.target.value }))}
                        placeholder="e.g. Revenue automation"
                        autoComplete="off"
                        required
                      />
                    </div>
                    <div className="wfp-field">
                      <label className="wfp-label" htmlFor="wfp-desc">
                        Description
                      </label>
                      <textarea
                        id="wfp-desc"
                        className="wfp-textarea"
                        value={form.description}
                        onChange={(ev) => setForm((f) => ({ ...f, description: ev.target.value }))}
                        placeholder="What this project is for…"
                      />
                    </div>
                    <div className="wfp-field">
                      <label className="wfp-label" htmlFor="wfp-owner">
                        Owner
                      </label>
                      <select
                        id="wfp-owner"
                        className="wfp-select"
                        value={form.owner}
                        onChange={(ev) => setForm((f) => ({ ...f, owner: ev.target.value }))}
                        disabled={loadingUsers}
                      >
                        <option value="">
                          {loadingUsers ? 'Loading users…' : 'No owner'}
                        </option>
                        {form.owner &&
                        !usersSorted.some((u) => u.id === form.owner) &&
                        !loadingUsers ? (
                          <option value={form.owner}>
                            {ownerDisplayLabel(form.owner, users)} (not in list)
                          </option>
                        ) : null}
                        {usersSorted.map((u) => (
                          <option key={u.id} value={u.id}>
                            {formatUserOptionLabel(u)}
                            {u.isActive === false ? ' · inactive' : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="wfp-field">
                      <span className="wfp-label">Icon</span>
                      <div className="wfp-icon-grid">
                        {PROJECT_ICON_OPTIONS.map(({ value, label, Icon }) => (
                          <button
                            key={value}
                            type="button"
                            className={`wfp-icon-btn${form.icon === value ? ' wfp-icon-btn--selected' : ''}`}
                            onClick={() => setForm((f) => ({ ...f, icon: value }))}
                            title={label}
                          >
                            <Icon className="w-4 h-4 shrink-0" strokeWidth={1.75} aria-hidden />
                            <span>{label}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="wfp-field">
                      <label className="wfp-label" htmlFor="wfp-status">
                        Status
                      </label>
                      <select
                        id="wfp-status"
                        className="wfp-select"
                        value={form.status}
                        onChange={(ev) => setForm((f) => ({ ...f, status: ev.target.value }))}
                      >
                        {STATUS_OPTIONS.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </div>
                  </>
                )}
              </form>

              <footer className="wfp-form-actions wfp-form-actions--split">
                <div>
                  {editingUuid ? (
                    <button
                      type="button"
                      className="wfp-btn wfp-btn--danger"
                      onClick={() => {
                        const p = projects.find((x) => x.uuid === editingUuid);
                        if (p) setProjectToDelete(p);
                      }}
                    >
                      <Trash2 className="w-4 h-4" aria-hidden />
                      Delete
                    </button>
                  ) : null}
                </div>
                <div className="wfp-form-actions__right">
                  <button type="button" className="wfp-btn wfp-btn--ghost" onClick={onClose}>
                    Close
                  </button>
                  <button
                    type="submit"
                    form="wfp-project-form"
                    className="wfp-btn wfp-btn--primary"
                    disabled={saving || loadingDetail}
                  >
                    {saving ? (
                      <>
                        <Loader2 className="wfp-spin w-4 h-4" aria-hidden />
                        Saving…
                      </>
                    ) : editingUuid ? (
                      'Save changes'
                    ) : (
                      'Create project'
                    )}
                  </button>
                </div>
              </footer>
            </div>
          </div>
        </div>
      </div>

      {projectToDelete ? (
        <ConfirmDialog
          title="Delete project"
          message={`Are you sure you want to delete "${projectToDelete.name}"? This action cannot be undone.`}
          onYes={() => void handleDelete()}
          onNo={() => setProjectToDelete(null)}
          onCancel={() => setProjectToDelete(null)}
        />
      ) : null}
    </>,
    document.body
  );
};

export default WorkflowProjectsModal;
